/**
 * Server function for fetching coach progress (Getting Started checklist,
 * marketplace approval state, and Profile Quality score).
 *
 * Aliases: coach progress, getting started, onboarding, profile quality
 */
import { createServerFn } from "@tanstack/react-start";

import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import type { CoachProgress } from "~/lib/shared/coach-progress-types";

export const getCoachProgress = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }): Promise<CoachProgress> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { eq, sql } = await import("drizzle-orm");
    const { coaches, certifications, coachesCcos } =
      await import("~/../drizzle/schema");
    const {
      calculateGettingStarted,
      deriveMarketplaceStatus,
      calculateProfileQuality,
    } = await import("~/lib/coach-progress");

    const coachShared = context.coach;
    if (!coachShared) {
      throw new Error("Coach not found");
    }

    const db = await getDBWithInit();

    const coach = await db.query.coaches.findFirst({
      where: { id: coachShared.id },
      with: { profile: true, details: true, mainOffer: true, expertises: true },
    });
    if (!coach) {
      throw new Error("Coach not found");
    }

    const [certCount] = await db
      .select({ count: sql<number>`count(*)::int` })
      .from(certifications)
      .where(eq(certifications.coachId, coach.id));

    const [ccoCount] = await db
      .select({ count: sql<number>`count(*)::int` })
      .from(coachesCcos)
      .where(eq(coachesCcos.coachId, coach.id));

    const gettingStarted = calculateGettingStarted(
      {
        ...coach,
        expertises: coach.expertises.map((e) => ({ id: e.subcategoryId })),
      },
      coach.profile,
      coach.details,
      {
        hasCertifications: (certCount?.count ?? 0) > 0,
        hasCcoMemberships: (ccoCount?.count ?? 0) > 0,
      },
    );

    const marketplaceStatus = deriveMarketplaceStatus(
      {
        marketplaceApprovedAt: coach.marketplaceApprovedAt ?? null,
        marketplaceRevokedAt: coach.marketplaceApprovalRevokedAt ?? null,
        marketplaceReturnedAt: coach.marketplaceApprovalReturnedAt ?? null,
        marketplaceRequestedAt: coach.marketplaceApprovalRequestedAt ?? null,
      },
      gettingStarted.completedCount === gettingStarted.totalCount,
    );

    const profileQuality = calculateProfileQuality(
      coach.profile,
      coach.details,
      coach.mainOffer,
      coach.expertises,
    );

    // Auto-mark onboarding complete on first full checklist completion
    if (
      gettingStarted.completedCount === gettingStarted.totalCount &&
      !coach.onboarding_completed_at
    ) {
      await db
        .update(coaches)
        .set({ onboarding_completed_at: new Date() })
        .where(eq(coaches.id, coach.id));
    }

    return { gettingStarted, marketplaceStatus, profileQuality };
  });
